'use client'

import Link from 'next/link'
import { useState } from 'react'

import { useSignOut } from '@/features/auth'
import { cn, useDialog } from '@/shared/lib'
import { ConfirmDialog, DropdownMenu, Icon } from '@/shared/ui'
import { buttonBase } from '@/shared/ui/button'

export const createPath = (isHostMode: boolean) => {
  const prefix = isHostMode ? '/host' : '/guest'

  return {
    account: `${prefix}/my/account`,
    favoriteRoom: `${prefix}/my/favorite-room`,
    recentRoom: `${prefix}/my/recent-room`,
    pay: `${prefix}/pay`,
    notice: `${prefix}/notice`,
    support: `${prefix}/support`,
    faq: `${prefix}/support/faq`,
    event: `${prefix}/campaign/event`,
    article: `${prefix}/campaign/article`,
  } as const
}

const DIALOG = {
  REQUEST_SIGN_OUT: 'REQUEST_SIGN_OUT',
} as const

type ProfileDialog = keyof typeof DIALOG

type Props = {
  isLoggedIn: boolean
  isHostMode: boolean
}

export const ProfileButton = ({ isLoggedIn, isHostMode }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSignOutRequested, setIsSignOutRequested] = useState(false)

  const path = createPath(isHostMode)
  const { isOpen, open, close } = useDialog<ProfileDialog>()

  const signOut = useSignOut()

  const handleRequestSignOut = () => {
    setIsSignOutRequested(true)
  }

  const handleDropdownOpenChange = (openState: boolean) => {
    setIsMenuOpen(openState)

    if (!openState && isSignOutRequested) {
      open(DIALOG.REQUEST_SIGN_OUT)
      setIsSignOutRequested(false)
    }
  }

  const handleSignOutConfirm = () => {
    signOut.execute(undefined)
  }

  const closeDialog = (key: ProfileDialog) => () => {
    close(key)
  }

  return (
    <>
      <DropdownMenu.Root
        open={isMenuOpen}
        onOpenChange={handleDropdownOpenChange}
      >
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              buttonBase,
              'group w-[49px] justify-start overflow-hidden rounded-[61px] p-0 shadow-[0px_1px_5px_0px_rgba(0,0,0,0.25)]',
              '[&_svg:first-child]:h-[28px] [&_svg:first-child]:w-[38px]',
              'data-[state=open]:bg-purple-50',
            )}
          >
            <Icon name="profile" strokeWidth={1.5} />
            <Icon
              name="arrow-bottom"
              size={14}
              strokeWidth={1.5}
              className="-ml-[9px] h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180"
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content align="end" className="w-[163px]">
          {!isLoggedIn && (
            <>
              <DropdownMenu.Item asChild>
                <Link href="/sign-in">로그인/회원가입</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
            </>
          )}
          {isHostMode ? (
            <>
              <DropdownMenu.Item asChild>
                <Link href={path.pay}>정산</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item asChild>
                <Link href={path.account}>계정 정보</Link>
              </DropdownMenu.Item>
            </>
          ) : (
            <>
              <DropdownMenu.Item asChild>
                <Link href={path.account}>계정 정보</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={path.favoriteRoom}>찜한 방</Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={path.recentRoom}>최근 본 방</Link>
              </DropdownMenu.Item>
            </>
          )}

          <DropdownMenu.Separator />

          <DropdownMenu.Item asChild>
            <Link href={path.faq}>자주 묻는 질문</Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={path.support}>고객센터</Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator />

          <DropdownMenu.Item asChild>
            <Link href={path.notice}>공지사항</Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={path.event}>이벤트/제휴</Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={path.article}>삼삼엠투 단기임대 이야기</Link>
          </DropdownMenu.Item>

          {isLoggedIn && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onClick={handleRequestSignOut}>
                로그아웃
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <ConfirmDialog
        isOpen={isOpen(DIALOG.REQUEST_SIGN_OUT)}
        onOpenChange={closeDialog(DIALOG.REQUEST_SIGN_OUT)}
        content="로그아웃 하시겠습니까?"
        cancelText="취소"
        confirmText="로그아웃"
        onConfirm={handleSignOutConfirm}
      />
    </>
  )
}
